import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from '../../core/services/data.service';
import { NotificationService } from '../../core/services/notification.service';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/Observable';
import { filter, map, take, tap } from 'rxjs/operators';
import { NotificationType } from '../../core/services/notification-type';
import { DataSourceRequestState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ListViewModel } from '../models/core/ListViewModel';
import { ActionDataResult } from '../models/core/ActionDataResult';
import { LeadListItem } from '../models/lead/lead-list-item';
import { LeadDetails } from '../models/lead/lead-details';
import { KeyValueObject } from '../models/core/KeyValueObject';

@Injectable()
export class LeadsService extends DataService<LeadListItem, LeadDetails> {
	constructor(http: HttpClient, notificationService: NotificationService) {
		super(http, notificationService);
		this.controllerName = 'leads';
	}

	public leadList(state: DataSourceRequestState, visibleColumnNames: string[]): Observable<ListViewModel<LeadListItem>> {
		let queryStr = `${toDataSourceRequestString(state)}`;
		queryStr = queryStr.replace(/T\d\d-00-0/g, 'T00-00-00');

		return this.http.post<ActionDataResult<ListViewModel<LeadListItem>>>(`${environment.apiUrl}v${this.controllerVersion}/${this.controllerName}/list`, {
			state: queryStr,
			visibleColumnNames: visibleColumnNames
		})
			.pipe(tap(resp => {
				if (!resp.isSuccessful) {
					this.notificationService.error({
						title: 'Ошибка',
						message: resp.errorDescription,
						notificationType: NotificationType.SweetAlert
					});
				}
			}, errorResponse => {
				if (errorResponse.status === 904) {
					return;
				}

				this.notificationService.error({
					title: 'Ошибка',
					message: errorResponse.error.errorDescription,
					notificationType: NotificationType.SweetAlert
				});
			}))
			.pipe(take(1))
			.pipe(filter(x => x.isSuccessful))
			.map(x => x.data);
	}

	public getLeadById(leadId: number): Observable<LeadDetails> {
		return this.getQuery<LeadDetails>(`${this.baseUrl()}/${leadId}/details`);
	}

	public addComment(leadId: number, text: string): Observable<ActionDataResult<any>> {
		return this.postQuery<any>(`${this.baseUrl()}/${leadId}/comments`, { text }, 'Комментарий добавлен');
	}

	public linkContragent(leadId: number, contragentId: number): Observable<ActionDataResult<any>> {
		return this.postQuery<any>(`${this.baseUrl()}/${leadId}/link-contragent`, { contragentId }, 'Лид связан с клиентом');
	}

	public postpone(leadId: number, stoppedToDateTime: string): Observable<ActionDataResult<any>> {
		return this.postQuery<any>(`${this.baseUrl()}/${leadId}/postpone`, { stoppedToDateTime }, 'Лид отложен');
	}

	public archive(leadId: number, comment: string): Observable<ActionDataResult<any>> {
		return this.postQuery<any>(`${this.baseUrl()}/${leadId}/archive`, { comment }, 'Лид архивирован');
	}

	public assignServiceCenter(leadId: number, serviceCenterId: number): Observable<{ serviceCenterId?: number; serviceCenterName?: string }> {
		return this.putQuery<{ serviceCenterId?: number; serviceCenterName?: string }>(
			`${this.baseUrl()}/${leadId}/service-center`,
			{ serviceCenterId }
		).pipe(
			take(1),
			map(resp => {
				if (!resp.isSuccessful) {
					throw new Error(resp.errorDescription || 'Не удалось сохранить сервисный центр');
				}
				return resp.data;
			})
		);
	}

	public getAssignableServiceCenters(): Observable<KeyValueObject[]> {
		return this.http.get<{ data?: KeyValueObject[] }>(`${this.baseUrl()}/service-centers-lookup`).pipe(
			take(1),
			map(resp => (resp && resp.data) ? resp.data : [])
		);
	}

	public assignInitType(leadId: number, initTypeId?: number, initTypeName?: string): Observable<LeadDetails> {
		const body: { initTypeId?: number; initTypeName?: string } = {};
		if (initTypeId != null) {
			body.initTypeId = initTypeId;
		}
		if (initTypeName != null && initTypeName.trim()) {
			body.initTypeName = initTypeName.trim();
		}
		return this.putQuery<LeadDetails>(`${this.baseUrl()}/${leadId}/init-type`, body)
			.pipe(
				take(1),
				map(resp => {
					if (!resp.isSuccessful) {
						throw new Error(resp.errorDescription || 'Не удалось сохранить источник');
					}
					return resp.data;
				})
			);
	}
}
